import { BrandGavel } from "./BrandGavel";

/**
 * MinutesArtifact — a stylised, on-brand mockup of what a Boardroom minutes
 * document looks like. Shown in the dashboard hero for zero-session users so
 * they see the artefact they're going to produce, not just read claims about
 * it.
 *
 * Pure SVG-in-JSX (no images, no lottie) — stays crisp at any DPR, ships
 * with the bundle, works offline.
 */
export default function MinutesArtifact({ className = "", testId }) {
  return (
    <div
      className={`relative w-full max-w-[380px] aspect-[3/4] ${className}`}
      data-testid={testId || "minutes-artifact"}
    >
      {/* Subtle gold glow behind the card. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -m-6 bg-[radial-gradient(ellipse_at_center,rgba(200,159,69,0.10),transparent_70%)] pointer-events-none"
      />

      {/* Document card — feels like heavy stock paper (cream ivory). */}
      <div className="relative w-full h-full rounded-md overflow-hidden shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)] rotate-[-2deg]">
        {/* Cream body */}
        <div className="absolute inset-0 bg-[#f7f2e6]" />
        {/* Grain overlay — very subtle */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.05] mix-blend-multiply"
          style={{
            backgroundImage: "radial-gradient(rgba(0,0,0,0.6) 1px, transparent 1px)",
            backgroundSize: "3px 3px",
          }}
        />

        {/* Gold header bar */}
        <div className="absolute inset-x-0 top-0 h-14 bg-[#0f0d09] flex items-center gap-3 px-5">
          <BrandGavel size={22} />
          <div className="flex flex-col leading-none">
            <span className="font-serif text-white text-sm">Boardroom</span>
            <span className="text-[10px] sm:text-[9px] sm:text-[8px] font-mono uppercase tracking-[0.2em] text-[#c89f45] mt-1">
              Meeting Minutes
            </span>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[#c89f45]" />
            <span className="h-1.5 w-1.5 rounded-full bg-[#3a3a3a]" />
            <span className="h-1.5 w-1.5 rounded-full bg-[#3a3a3a]" />
          </div>
        </div>

        {/* Body */}
        <div className="absolute inset-x-0 top-14 bottom-0 px-6 py-5 flex flex-col gap-4">
          {/* Meta line */}
          <div className="flex items-center justify-between">
            <span className="text-[10px] sm:text-[9px] sm:text-[8px] font-mono uppercase tracking-[0.2em] text-[#8a6c1f]">
              § 01 · Q1 · Week 4
            </span>
            <span className="text-[10px] sm:text-[9px] sm:text-[8px] font-mono uppercase tracking-widest text-[#8a8a8a]">
              Solo · 28 min
            </span>
          </div>

          {/* Serif headline */}
          <h4 className="font-serif text-[#1a1712] text-lg leading-tight">
            The shipping cadence held.
          </h4>

          {/* Body paragraphs — simulated with tinted bars */}
          <div className="flex flex-col gap-1.5">
            <BodyLine w="w-full" />
            <BodyLine w="w-[92%]" />
            <BodyLine w="w-[85%]" />
            <BodyLine w="w-[70%]" />
          </div>

          {/* Wins eyebrow + bullets */}
          <div className="mt-1">
            <p className="text-[10px] sm:text-[9px] font-mono uppercase tracking-[0.2em] text-[#8a6c1f] mb-1.5">
              Wins
            </p>
            <div className="flex flex-col gap-1">
              <BulletLine w="w-[88%]" />
              <BulletLine w="w-[72%]" />
              <BulletLine w="w-[60%]" />
            </div>
          </div>

          {/* Action items */}
          <div className="mt-1">
            <p className="text-[10px] sm:text-[9px] font-mono uppercase tracking-[0.2em] text-[#8a6c1f] mb-1.5">
              Action items
            </p>
            <div className="flex flex-col gap-1.5">
              <ActionLine w="w-[85%]" />
              <ActionLine w="w-[78%]" checked />
              <ActionLine w="w-[65%]" />
            </div>
          </div>

          {/* Sign-off */}
          <div className="mt-auto flex items-end justify-between">
            <div>
              <p className="text-[10px] sm:text-[9px] sm:text-[8px] font-mono uppercase tracking-[0.2em] text-[#8a8a8a]">
                Signed · Chair
              </p>
              <div className="mt-2 h-4 w-24 border-b border-[#1a1712]" />
            </div>
            <StampBadge />
          </div>
        </div>

        {/* Right-edge fold hint */}
        <div
          aria-hidden="true"
          className="absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-black/[0.03] to-transparent pointer-events-none"
        />
      </div>

      {/* Floating "AI-drafted" chip — off-card, top-right */}
      <div
        aria-hidden="true"
        className="absolute -top-3 -right-3 sm:right-2 rotate-[6deg] bg-[#0f0d09] border border-[#c89f45]/40 rounded-full px-3 py-1 text-[10px] sm:text-[9px] font-mono uppercase tracking-[0.2em] text-[#c89f45] shadow-lg"
      >
        AI · minutes
      </div>

      {/* Floating "action items" chip — bottom-left */}
      <div
        aria-hidden="true"
        className="absolute -bottom-3 left-2 sm:-left-2 -rotate-[4deg] bg-[#0f0d09] border border-[#2a2a2a] rounded-full px-3 py-1 text-[10px] sm:text-[9px] font-mono uppercase tracking-[0.2em] text-[#eaeaea] shadow-lg"
      >
        Signed off · today
      </div>
    </div>
  );
}


function BodyLine({ w = "w-full" }) {
  return <div className={`h-1 rounded-full bg-[#c9beaa] ${w}`} />;
}

function BulletLine({ w = "w-full" }) {
  return (
    <div className="flex items-center gap-2">
      <span className="h-1 w-1 rounded-full bg-[#8a6c1f] shrink-0" />
      <div className={`h-1 rounded-full bg-[#a89f89] ${w}`} />
    </div>
  );
}

function ActionLine({ w = "w-full", checked = false }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={`h-2.5 w-2.5 rounded-sm border ${
          checked ? "bg-[#c89f45] border-[#8a6c1f]" : "border-[#a89f89]"
        } shrink-0`}
      />
      <div className={`h-1 rounded-full bg-[#a89f89] ${w}`} />
    </div>
  );
}

function StampBadge() {
  return (
    <div className="flex items-center gap-2 rounded-full border-2 border-[#c89f45]/70 px-2.5 py-1 -rotate-[8deg]">
      <span className="text-[10px] sm:text-[9px] sm:text-[8px] font-mono uppercase tracking-[0.2em] text-[#8a6c1f]">
        Session Held
      </span>
    </div>
  );
}
